import { type MouseEvent, useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { ApiEndpoints } from '@avisoma/shared';
import backend from '../../services/backend';
import { useInfiniteList } from '../../hooks/useInfiniteList';
import { InfinityScroll } from '../../components/InfinityScroll';
import { RowInspectorDialog } from '../../components/reconstructedDocument/extractionTables/RowInspectorDialog';
import { useAuth } from '../../hooks/useAuth';
import { WebhookPayloadDialog } from './WebhookPayloadDialog';
import { Badge } from '../../catalyst/badge';
import { Button } from '../../catalyst/button';
import FileSize from '../../components/FileSize';
import { formatDateTime } from '../../lib/dateFormat';
import { WebhookDeliveriesChart, type Breakdown, type Zoom } from './WebhookDeliveriesChart';
import { WebhookEventTypeChips } from './WebhookEventTypeChips';
import {
  extractionRowClass,
  extractionTableClass,
  extractionTbodyClass,
  extractionTdClass,
  extractionThClass,
  extractionTheadClass,
} from '../../components/reconstructedDocument/extractionTables/tableClasses';

type WebhookDelivery =
  ApiEndpoints<string>['/api/projects/:id/webhook-deliveries']['GET']['response']['deliveries'][number];

// The list endpoint caps a single response at 200 rows (MAX_LIMIT). The
// infinite-list hook grows its window unboundedly, so we page the log in
// 200-row chunks via offset and concatenate to satisfy an arbitrarily large
// window, rather than asking for one oversized limit the server would clamp.
const SERVER_MAX_LIMIT = 200;
const INITIAL_ITEMS_PER_PAGE = 50;

type DeliveryFilters = { eventType?: string; from?: string; to?: string };

// Assemble the newest `limit` deliveries by walking the log in <=200-row pages.
// Newest-first ordering is stable across pages because the endpoint always
// orders by createdAt desc, and `total` (the unpaginated count) tells us when
// more rows remain beyond the current window. Refetched whole on every window
// bump / reset, so auto-refresh always reflects the full visible window.
async function fetchDeliveryWindow(
  projectId: string,
  limit: number,
  filters: DeliveryFilters,
): Promise<{ items: WebhookDelivery[]; hasMore: boolean }> {
  const fetchFrom = async (
    offset: number,
    acc: WebhookDelivery[],
  ): Promise<{ items: WebhookDelivery[]; total: number }> => {
    const { deliveries, total } = await backend
      .getWebhookDeliveries(projectId, {
        limit: Math.min(SERVER_MAX_LIMIT, limit - offset),
        offset,
        eventType: filters.eventType,
        from: filters.from,
        to: filters.to,
      })
      .then((res) => res.data);
    const items = [...acc, ...deliveries];
    // Stop once the window is filled, the server ran out of rows (short/empty
    // page), or we've reached the total under this filter.
    if (items.length >= limit || deliveries.length === 0 || items.length >= total) {
      return { items, total };
    }
    return fetchFrom(items.length, items);
  };
  const { items, total } = await fetchFrom(0, []);
  return { items, hasMore: items.length < total };
}

function outcomeColor(outcome: string): 'green' | 'emerald' | 'amber' | 'red' | 'zinc' {
  switch (outcome) {
    case 'success':
      return 'green';
    case 'published':
      return 'emerald';
    case 'retry':
      return 'amber';
    case 'dropped':
    case 'publish-failed':
      return 'red';
    default:
      return 'zinc';
  }
}

function TabEmptyState({ label }: { label: string }) {
  return (
    <div className="flex min-h-[160px] items-center justify-center rounded-lg border border-dashed border-gray-200 px-6 py-8 text-sm text-gray-500 dark:border-zinc-700 dark:text-zinc-400">
      No {label} for this project
    </div>
  );
}

export function WebhookDeliveries({ projectId }: { projectId: string }) {
  const { hasAccess } = useAuth();
  const [eventType, setEventType] = useState<string>('');
  const [breakdown, setBreakdown] = useState<Breakdown>('outcome');
  const [zoom, setZoom] = useState<Zoom | null>(null);
  const [inspected, setInspected] = useState<WebhookDelivery | null>(null);
  const [payloadDeliveryId, setPayloadDeliveryId] = useState<number | null>(null);

  // The chart's zoom window belongs to the project it was selected on — drop it
  // when the tab is reused for another project (render-time reset, not an
  // effect). The tab isn't remounted on project change, so this state must be
  // reset explicitly.
  const [prevProjectId, setPrevProjectId] = useState(projectId);
  if (prevProjectId !== projectId) {
    setPrevProjectId(projectId);
    setZoom(null);
    setEventType('');
    setBreakdown('outcome');
    setInspected(null);
    setPayloadDeliveryId(null);
  }

  // Stats fetch lifted out of the chart so the chart and the event-type chip
  // row share one request. zoom in the key so a deeper window refetches; with no
  // zoom the request is `range=auto` — the server buckets the project's full
  // delivery span.
  const {
    data: statsData,
    isLoading: statsLoading,
    isPlaceholderData: statsPlaceholder,
  } = useQuery({
    queryKey: [
      'webhook-delivery-stats',
      projectId,
      eventType || null,
      zoom?.from ?? null,
      zoom?.to ?? null,
      breakdown,
    ],
    queryFn: () =>
      backend
        .getWebhookDeliveryStats(
          projectId,
          zoom
            ? { from: zoom.from, to: zoom.to, eventType: eventType || undefined, groupBy: breakdown }
            : { range: 'auto', eventType: eventType || undefined, groupBy: breakdown },
        )
        .then((res) => res.data),
    placeholderData: keepPreviousData,
  });

  const queryKey = useMemo(
    () => ['webhook-deliveries', projectId, eventType, zoom?.from ?? null, zoom?.to ?? null],
    [projectId, eventType, zoom?.from, zoom?.to],
  );

  // Primitive resetKey so the window snaps back to the first page whenever the
  // event-type filter OR the chart zoom changes (an object literal would get a
  // fresh identity every render and reset pagination on every render).
  const resetKey = `${eventType}|${zoom?.from ?? ''}|${zoom?.to ?? ''}`;

  const { items, hasMore, loading, refreshing, loadMore } = useInfiniteList<WebhookDelivery>({
    queryKey,
    queryFn: (limit) =>
      fetchDeliveryWindow(projectId, limit, {
        eventType: eventType || undefined,
        from: zoom?.from,
        to: zoom?.to,
      }),
    enabled: true,
    initialItemsPerPage: INITIAL_ITEMS_PER_PAGE,
    resetKey,
  });

  return (
    <div className="space-y-4">
      <WebhookDeliveriesChart
        breakdown={breakdown}
        onBreakdownChange={setBreakdown}
        zoom={zoom}
        onZoomChange={setZoom}
        data={statsData}
        isLoading={statsLoading}
        isPlaceholderData={statsPlaceholder}
      />

      {statsData && (
        <WebhookEventTypeChips
          eventTypeCounts={statsData.eventTypeCounts}
          selected={eventType}
          onSelect={setEventType}
          dimmed={statsPlaceholder}
        />
      )}

      {/* refreshing + empty = a held-over empty placeholder while the new
          window fetches — not a settled result, so keep showing the loader. */}
      {(loading || refreshing) && items.length === 0 ? (
        <div className="flex min-h-[160px] items-center justify-center rounded-lg border border-dashed border-gray-200 px-6 py-8 text-sm text-gray-500 dark:border-zinc-700 dark:text-zinc-400">
          Loading webhook deliveries…
        </div>
      ) : items.length === 0 ? (
        zoom ? (
          <div className="flex min-h-[160px] items-center justify-center rounded-lg border border-dashed border-gray-200 px-6 py-8 text-sm text-gray-500 dark:border-zinc-700 dark:text-zinc-400">
            No webhook deliveries between {formatDateTime(zoom.from)} and {formatDateTime(zoom.to)} (use “Reset
            zoom” to clear)
          </div>
        ) : (
          <TabEmptyState label="webhook deliveries" />
        )
      ) : (
        // Dim held-over rows while a zoom/filter change refetches, so stale
        // entries aren't misread as belonging to the labeled window.
        <div
          className={`bg-white dark:bg-zinc-800 shadow dark:shadow-zinc-950/30 rounded-lg overflow-hidden border border-gray-200 dark:border-zinc-700 transition-opacity ${
            refreshing ? 'opacity-40' : ''
          }`}
        >
          <div className="px-4 py-3 border-b border-gray-200 dark:border-zinc-700">
            <h2 className="text-sm font-medium text-gray-900 dark:text-zinc-100">
              Webhook Deliveries ({items.length}
              {hasMore ? '+' : ''})
            </h2>
            {zoom && (
              <p className="mt-0.5 text-xs text-gray-500 dark:text-zinc-400">
                Filtered to {formatDateTime(zoom.from)} – {formatDateTime(zoom.to)} (use “Reset zoom” to clear)
              </p>
            )}
          </div>
          <div className="max-h-[650px] overflow-auto">
            <table className={extractionTableClass}>
              <thead className={extractionTheadClass}>
                <tr>
                  <th className={extractionThClass}>Time</th>
                  <th className={extractionThClass}>Event Type</th>
                  <th className={extractionThClass}>Transport</th>
                  <th className={extractionThClass}>Outcome</th>
                  <th className={extractionThClass}>Status</th>
                  <th className={extractionThClass}>Attempt</th>
                  <th className={extractionThClass}>Target</th>
                  <th className={extractionThClass}>Error</th>
                  <th className={extractionThClass}>Payload</th>
                </tr>
              </thead>
              <tbody className={extractionTbodyClass}>
                {items.map((delivery) => (
                  <tr
                    key={delivery.id}
                    onClick={() => setInspected(delivery)}
                    className={extractionRowClass}
                  >
                    <td className={`${extractionTdClass} whitespace-nowrap`}>
                      {formatDateTime(delivery.createdAt)}
                    </td>
                    <td className={extractionTdClass}>{delivery.eventType}</td>
                    <td className={extractionTdClass}>{delivery.transport}</td>
                    <td className={extractionTdClass}>
                      <Badge color={outcomeColor(delivery.outcome)}>{delivery.outcome}</Badge>
                    </td>
                    <td className={`${extractionTdClass} font-mono`}>{delivery.statusCode ?? '—'}</td>
                    <td className={`${extractionTdClass} font-mono`}>{delivery.attempt}</td>
                    <td
                      className={`${extractionTdClass} max-w-[16rem] truncate font-mono`}
                      title={delivery.target}
                    >
                      {delivery.target}
                    </td>
                    <td
                      className={`px-2 py-1.5 max-w-[20rem] truncate ${delivery.error ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-zinc-100'}`}
                      title={delivery.error ?? undefined}
                    >
                      {delivery.error ?? '—'}
                    </td>
                    <td className={extractionTdClass}>
                      {delivery.payloadHash && hasAccess('admin') ? (
                        <span className="inline-flex items-center gap-1 whitespace-nowrap">
                          <Button
                            plain
                            onClick={(e: MouseEvent) => {
                              e.stopPropagation();
                              setPayloadDeliveryId(delivery.id);
                            }}
                          >
                            View
                          </Button>
                          {delivery.payloadBytes != null && (
                            <span className="text-xs text-gray-400 dark:text-zinc-500">
                              · <FileSize bytes={delivery.payloadBytes} />
                            </span>
                          )}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <InfinityScroll onLoadMore={loadMore} enabled={hasMore && !loading}>
              <div className="h-4" />
            </InfinityScroll>
          </div>
        </div>
      )}

      <RowInspectorDialog
        open={inspected != null}
        onClose={() => setInspected(null)}
        title="Webhook delivery"
        data={inspected}
      />

      <WebhookPayloadDialog
        projectId={projectId}
        deliveryId={payloadDeliveryId}
        open={payloadDeliveryId != null}
        onClose={() => setPayloadDeliveryId(null)}
      />
    </div>
  );
}
